import IdeaProperty from "@/types/IdeaProperty";
import {asArray} from "@/utils/arrayUtils";
import Domain, {DomainOption, domainOfProperty} from "@/types/Domain";
import {instanceColor} from "@/utils/colors";
import {t} from "@/i18n";
import {WorkspacePerson} from "@/types/Person";
import {asBoolean} from "@/utils/utils";

/**
 * Regras de leitura de um valor de propriedade, extraídas das três cópias que
 * existiam em ListInstances, InstanceView e no simpleValueString. Aqui só mora o
 * que é decisão de dado; como cada tela desenha fica no PropertyValue.vue.
 */

export type ValueKind = 'rel' | 'choice' | 'user' | 'date' | 'url' | 'image' | 'audio' | 'markdown' | 'boolean' | 'text'

/**
 * Onde um valor guardado como id acha o nome: os domínios, para a seleção; as pessoas do ambiente,
 * para o atributo de usuário. O próprio `workspace` tem essa forma, e é o que a maioria passa.
 */
export type ValueLookup = { domains?: Domain[], people?: WorkspacePerson[] }

/**
 * Campo de ESCOLHA: o valor é um id escolhido de uma lista — o item, num relacionamento; a opção
 * do domínio, numa seleção; a pessoa do ambiente, num atributo de usuário. Filtra por lista de ids,
 * vira raia no Kanban, não é faixa, tem "Opções permitidas". O par no servidor é o
 * `IdeaProperty.choiceField`.
 *
 * Uma pergunta com dono: estava escrita à mão (`relationship || type === 'choice'`) em cada tela, e
 * o tipo usuário teria de ser lembrado em todas.
 */
export function isChoiceField(property: IdeaProperty): boolean {
    return !!property.relationship || property.type === 'choice' || property.type === 'user'
}

/**
 * A pessoa que um valor de usuário aponta — o item guarda só o id da conta; nome, e-mail e foto
 * vêm das pessoas do ambiente. Conta que não está lá devolve undefined, e a tela a trata como vazia.
 */
export function personOf(value: any, people?: WorkspacePerson[]): WorkspacePerson | undefined {
    if (isEmpty(value)) return undefined
    return (people || []).find(person => person.user === Number(value))
}

/**
 * O nome de uma pessoa do ambiente como se lê. Sem nome, uma frase fixa: o e-mail não vai ao modo de
 * uso, e o nome é obrigatório na conta — só falta em quem ainda não o preencheu.
 */
export function personName(person: WorkspacePerson): string {
    return person.name || t('person-unnamed') as string
}

export function displayKind(property: IdeaProperty): ValueKind {
    if (property.relationship) return 'rel'
    if (property.type === 'choice') return 'choice'
    if (property.type === 'user') return 'user'
    if (property.type === 'date') return 'date'
    if (property.type === 'boolean') return 'boolean'
    if (property.type === 'image') return 'image'
    if (property.type === 'audio') return 'audio'
    if (property.type === 'text' && property.subtype === 'markdown') return 'markdown'
    if (property.type === 'text' && property.subtype === 'url') return 'url'
    return 'text'
}

/**
 * Os valores que se delimitam SOZINHOS, e por isso não querem separador entre si.
 *
 * Seleção vira chip tingido — ou bolinha e nome, no card do Kanban. Relacionamento leva a
 * régua do item à esquerda. Usuário leva o avatar. Mídia leva os próprios controles. Em todos já
 * se vê onde um valor acaba e o outro começa, e a barra passa a ser ruído dentro da célula.
 */
const SELF_DELIMITED: ValueKind[] = ['rel', 'choice', 'user', 'image', 'audio']

/**
 * Entre dois valores deste campo cabe um separador?
 *
 * Só onde o valor é texto corrido: "Ana" e "Bia" lado a lado se leriam como um nome só, e é
 * para isso que a barra existe.
 *
 * **A regra estava escrita em três lugares, e já havia divergido**: a listagem tirava a barra
 * do relacionamento e da mídia, mas não da seleção; a ficha tirava só do relacionamento; e o
 * card do Kanban a punha entre tudo, inclusive entre dois chips. Três telas discordando sobre
 * a mesma pergunta — agora ela tem um dono.
 *
 * Quebra de linha é OUTRA decisão, e continua de quem desenha: a ficha põe valor longo e mídia
 * um por linha, e isso é espaço, não delimitação.
 */
export function needsValueSeparator(property: IdeaProperty): boolean {
    return SELF_DELIMITED.indexOf(displayKind(property)) < 0
}

/**
 * Relacionamento vem em _rels (com _identifier e _state); o resto vem direto do
 * documento. A ordem importa: o campo cru de um relacionamento guarda só o id.
 */
export function propertyValue(instance: any, alias: string): any {
    return (instance._rels && instance._rels[alias]) || instance[alias]
}

/**
 * Quantos relacionados deste campo quem olha não vê — o servidor manda a contagem em `_hidden`, e os
 * itens não. Zero quando não há oculto, ou o campo não é relacionamento.
 */
export function hiddenCount(instance: any, property: { alias: string }): number {
    return (instance && instance._hidden && instance._hidden[property.alias]) || 0
}

/** Sempre uma lista, mesmo para propriedade simples, para o v-for do chamador. */
export function displayValues(instance: any, property: IdeaProperty): any[] {
    return asArray(propertyValue(instance, property.alias))
}

/**
 * O id do item apontado por um valor de relacionamento.
 *
 * O valor chega de duas formas: o objeto resolvido do `_rels`, com `_instance` dentro, ou o id
 * cru do campo. Desembrulhar isso é pergunta de TODA tela que desenha um relacionamento — o
 * chip, a bolinha do campo de escolha, a dimensão do Kanban —, e por isso mora aqui, ao lado do
 * `propertyValue`, e não dentro de uma delas.
 *
 * `undefined` quando não há valor: cada chamador decide o que isso significa para ele.
 */
export function relatedId(value: any): number | undefined {
    if (isEmpty(value)) return undefined
    const id = Number(value && typeof value === 'object' ? value._instance : value)
    return Number.isInteger(id) ? id : undefined
}

/**
 * A cor do item que este valor representa.
 *
 * Serve às duas formas em que um item aparece: o valor de um relacionamento — objeto do
 * `_rels` ou id cru — e a própria instância de uma linha de tabela. Junta as duas perguntas
 * que sempre andam juntas, qual é o id e qual é a cor dele, para o par não se repetir em cada
 * componente que desenha um item.
 */
export function instanceColorOf(value: any): string | undefined {
    const id = relatedId(value)
    return id === undefined ? undefined : instanceColor(id)
}

/**
 * O id e a cor de um item de lista do Vuetify — a opção de um campo de relacionamento, o escolhido
 * nele, o de uma janela de escolha.
 *
 * O slot tanto entrega o objeto cru quanto o item interno que o embrulha (`raw`), conforme o caminho.
 * Desembrulhar estava escrito em cada campo que desenha a régua da opção, e a terceira janela seria
 * a terceira cópia.
 */
export function optionIdOf(item: any): number | undefined {
    return relatedId(item && item.raw || item)
}

export function optionColorOf(item: any): string {
    return instanceColorOf(item && item.raw || item) || ''
}

/**
 * Os itens apontados por um relacionamento entre estas instâncias, sem repetição e na ordem em
 * que aparecem.
 *
 * É o que a listagem precisa para oferecer o filtro de um relacionamento — os valores possíveis
 * são os PRESENTES, porque a coleção de destino pode ter centenas de itens — e é a mesma
 * pergunta que a dimensão de relacionamento do Kanban faz para montar as raias. Era o
 * `uniqueRelValues` de dentro da listagem, que nunca foi uma regra de listagem.
 *
 * Deduplica pelo `_instance`, e por isso devolve o objeto inteiro: quem chama quer o
 * `_identifier` para desenhar e o `_state` para colorir.
 */
export function relatedValues(instances: any[], alias: string): any[] {
    const found = new Map<any, any>()
    instances.forEach(instance => asArray(propertyValue(instance, alias))
        .forEach(value => {
            const id = value && typeof value === 'object' ? value._instance : value
            if (!isEmpty(id) && !found.has(id)) found.set(id, value)
        }))
    return Array.from(found.values())
}

/**
 * Data é guardada como 'YYYY-MM-DD' puro, e o T00:00:00 é o que impede o browser
 * de ler como UTC e exibir o dia anterior. Não vale para _stateChangedAt e afins,
 * que são ISO datetime completos — esses passam por finishedPeriod.ts.
 */
export function formatSimple(property: IdeaProperty, value: any): string {
    if (isEmpty(value)) return ''
    if (property.type === 'date') return new Date(value + 'T00:00:00').toLocaleDateString()
    return String(value)
}

/**
 * Vazio: ausente, texto em branco, número inválido, ou lista sem nenhum valor de verdade. Falso NÃO
 * é vazio — no sim/não, Não é uma escolha (decisão do usuário).
 *
 * A definição de vazio do app inteiro: o que se exibe como nada, o que o obrigatório recusa, o que
 * o default preenche. Eram três, cada uma com uma resposta. O par no servidor é o `isEmptyValue`
 * (`lunedi/src/domain/fieldValidation.ts`): mudou aqui, muda lá.
 */
export function isEmpty(value: any): boolean {
    if (value === undefined || value === null) return true
    if (typeof value === 'number') return isNaN(value)
    if (Array.isArray(value)) return value.every(isEmpty)
    if (typeof value === 'string') return !value.trim()
    return false
}

/**
 * A opção que um valor de seleção aponta. O dado guarda só o id, e o nome e a cor moram
 * no domínio — por isso quem exibe precisa passar a lista de domínios.
 *
 * Opção que sumiu do domínio devolve undefined, e a tela a trata como vazia: o servidor
 * não deixa remover opção em uso, então isso só acontece com dado de antes dessa regra.
 */
export function choiceOption(property: IdeaProperty, value: any, domains?: Domain[]): DomainOption | undefined {
    if (isEmpty(value)) return undefined
    const domain = domainOfProperty(property, domains)
    return domain ? domain.optionWith(Number(value)) : undefined
}

/**
 * Texto de um valor para tooltip, truncagem, ordenação, exportação e busca — inclusive de
 * relacionamento.
 *
 * `lookup` só é lido para seleção (os domínios) e usuário (as pessoas). Sem ele, os dois dão texto
 * vazio, e não o id: número interno na tela seria pior que nada.
 */
export function valueText(property: IdeaProperty, value: any, lookup: ValueLookup = {}): string {
    if (isEmpty(value)) return ''
    if (property.relationship) return value._identifier || ''
    if (property.type === 'choice') return choiceOption(property, value, lookup.domains)?.name || ''
    if (property.type === 'user') {
        const person = personOf(value, lookup.people)
        return person ? personName(person) : ''
    }
    //A palavra da exibição, e não o `true` gravado
    if (property.type === 'boolean') return booleanFace(asBoolean(value)).label
    return formatSimple(property, value)
}

/**
 * Como um sim/não se mostra: o sinal, a cor e a palavra — ✓ Sim em verde, ✗ Não em vermelho. A cor
 * dá a leitura de relance e a palavra evita depender só dela.
 *
 * Um dono para a exibição (`PropertyValue`) e para os dois botões do formulário (`BooleanInput`): o
 * que se escolhe tem a cara do que se vê.
 */
export function booleanFace(value: boolean): { icon: string, color: string, label: string } {
    return value
        ? {icon: 'mdi-check', color: 'green-darken-2', label: t('yes')}
        : {icon: 'mdi-close', color: 'red-darken-2', label: t('no')}
}

/** Uma propriedade só ocupa espaço no card se tiver algum valor de verdade. */
export function hasValue(instance: any, property: IdeaProperty, lookup?: ValueLookup): boolean {
    return displayValues(instance, property).some(value => !isEmpty(value) && valueText(property, value, lookup) !== '')
}
